import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { captureMessage } from '@sentry/react';
import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import moment from 'moment';
import currency from 'currency.js';
import Pagination from "react-bootstrap/Pagination";
import { asyncDebouncer, exportCSVFile } from '../../utils/helper';
import DecimalInput from '../../common/DecimalInput';
import useDidMountEffect from '../../utils/useDidMountEffect';
import { useDispatch } from 'react-redux';
import { setAlert } from '../../../redux/actions';

const perPage = 50;

const sortingStyle = {
    color: 'rgb(0, 151, 254)',
    cursor: 'pointer',
    marginLeft: '8px',
};

const Sorting = ({ sort = 'ASC' }) => {
    return sort === 'ASC'
        ? <FontAwesomeIcon
            icon={["fas", "chevron-up"]}
            style={sortingStyle}
        />
        : <FontAwesomeIcon
            icon={["fas", "chevron-down"]}
            style={sortingStyle}
        />;
};

const tableHeaders = [
    {
        name: 'Tag',
        sortable: false,
        column: '',
        width: '20px',
    },
    {
        name: 'Follower ID',
        sortable: false,
        column: 'follower.id',
        width: '20px',
    },
    {
        name: 'SU',
        sortable: true,
        column: 'su_login',
        width: '20%',
    },
    {
        name: 'Creator',
        sortable: true,
        column: 'pu_login',
        width: '20%',
    },
    {
        name: 'Plan ID',
        sortable: true,
        column: 'follower.plan_id',
        width: '20%',
    },
    {
        name: 'Subscribe Date',
        sortable: true,
        column: 'follower.subscribed_at',
        width: '20%',
    },
    {
        name: 'Amount',
        sortable: true,
        column: 'follower.amount',
        width: '20%',
    }
];

const SuList = ({
    planName,
    status,
    refetch,
    setLoading,
    selectedSus,
    setSelectedSus,
    selectedPus,
    selectedPlan,
    setSelectedPlan,
    setSelectedFollower,
    selectedFollower
}) => {
    const dispatch = useDispatch();

    const [sus, setSus] = useState([]);
    const [gettingSuList, setGettingSuList] = useState(false);
    const [page, setPage] = useState(1);
    const [orderBy, setOrderBy] = useState('su_login');
    const [sortBy, setSortBy] = useState('ASC');
    const [total, setTotal] = useState(0);
    const [pageItems, setPageItems] = useState(0);
    const [tagAll, setTagAll] = useState(false);

    const handleClearAll = () => {
        setSelectedFollower([]);
    };

    const handleTagAll = () => {
        setTagAll(true);
    };

    const getSus = asyncDebouncer(async (reset = false) => {
        setLoading(true);
        axios.get('/api/administration/edit-followers', {
            params: {
                tag_all: tagAll,
                plan_name: selectedPlan.length > 0 ? selectedPlan[0].name : '',
                page,
                status,
                order_by: orderBy,
                sort_by: sortBy,
                pu: selectedPus && selectedPus.length > 0 ? selectedPus[0].id : ''
            }
        })

            .then(response => {
                if (tagAll) {
                    const all = response.data.map(su => su.id);

                    setSelectedFollower([...new Set([
                        ...selectedSus,
                        ...all
                    ])]);

                    setTagAll(false);
                } else {
                    setSus(response.data);
                    setTotal(response.data.total);
                    setPageItems(Math.ceil(response.data.total / perPage));
                }
                // setSus(response.data);
                setLoading(false);
            });
    }, 600);

    useDidMountEffect(() => {
        getSus();
    }, [page]);

    useDidMountEffect(() => {
        getSus(true);
    }, [sortBy, orderBy, refetch]);

    useDidMountEffect(() => {
        setIsBusy(gettingSuList);
    }, [gettingSuList]);

    const handleSorting = (e, column) => {
        e.preventDefault();

        if (orderBy === column) {
            if (sortBy === 'ASC') {
                setSortBy('DESC');
            } else {
                setSortBy('ASC');
            }
        } else {
            setOrderBy(column);
            setSortBy('ASC');
        }

        setPage(1);
    };

    const handleSelectFollower = (su) => {
        let tmpSelectedFollower = [...selectedFollower];

        const index = tmpSelectedFollower.indexOf(su.id);

        if (index >= 0) {
            tmpSelectedFollower.splice(index, 1);
        } else {
            tmpSelectedFollower.push(su.id);
        }

        setSelectedFollower(tmpSelectedFollower);
    };


    const handleNextPage = () => {
        if (sus.current_page < sus.last_page)
            setPage(sus.current_page + 1);
        // setRefetch(refetch => refetch + 1)
    };

    const handleLastPage = () => {
        setPage(sus.last_page);
        // setRefetch(refetch => refetch + 1)
    };

    const handlePrevPage = () => {
        if (sus.current_page > 1)
            setPage(sus.current_page - 1);
    };
    const handleFirstPage = () => {
        setPage(1);
    };

    const PaginationContainer = () => {
        const isPrevDisabled = page > 1 ? false : true;
        const isNextDisabled = page < sus.last_page ? false : true;
        const handleManualPage = (value) => {
            let tmpValue = value;
            if (tmpValue.length > 0) {
                tmpValue = tmpValue.toString().replace(/[^0-9+]/g, '');
                if (tmpValue) {
                    tmpValue = parseInt(tmpValue, 10);
                }
            }

            setPage(tmpValue);
        };
        return (
            <Pagination
                style={{ alignItems: "center", marginBottom: 0 }}
            >
                <span className="mr-3">
                    {total} item{total > 1 ? "s" : ""}
                </span>
                <Pagination.First
                    disabled={isPrevDisabled}
                    className="mr-1"
                    onClick={handleFirstPage}
                />
                <Pagination.Prev
                    disabled={isPrevDisabled}
                    onClick={handlePrevPage}
                />
                <div className="mx-2 d-flex flex-row align-items-center justify-content-center w-auto">
                    <div className="mr-1" style={{ width: 35 }}>
                        <DecimalInput
                            inputMode="numeric"
                            className="pagination-input"
                            value={page}
                            handleChange={handleManualPage}
                            bannedCharacters={['e', 'E', '+', '-', '.']}
                        />
                    </div>
                    of {pageItems}
                </div>
                <Pagination.Next
                    disabled={isNextDisabled}
                    onClick={handleNextPage}
                />
                <Pagination.Last
                    disabled={isNextDisabled}
                    className="ml-1"
                    onClick={handleLastPage}
                />
            </Pagination>
        );
    };

    useDidMountEffect(() => {
        if (tagAll) {
            getSus();
        } else {
            if (!sus.data || sus.data.length <= 0) {
                getSus();
            }
        }
    }, [tagAll]);

    useDidMountEffect(() => {
        if (selectedSus.length <= 0) {
            setSelectedSUsObject([]);
        }
    }, [selectedSus]);


    const handleRemoveSubscription = () => {
        axios.post('/api/administration/edit-followers', {
            followers: selectedFollower
        })

            .then(response => {
                dispatch(setAlert({
                    show: true,
                    type: 'success',
                    body: `Subscriptions queued for removal`
                }));
            })
            .catch(error => {
                dispatch(setAlert({
                    show: true,
                    type: 'error',
                    body: `Failed to remove subscriptions`
                }));
            });
    };

    return <React.Fragment>
        <div className="actions my-3">
            <button
                className="btn btn-primary"
                onClick={handleTagAll}
            >
                Tag All Users
            </button>
            <button
                className="btn btn-primary"
                onClick={handleClearAll}
            >
                Clear Tagged Selections
            </button>
            {/* <button
                className="btn btn-primary"
                onClick={handleExportTagged}
            >
                Export Tagged
            </button> */}
            {/* <button
                className="btn btn-primary"
                onClick={handleExportSuFollowingPu}
            >
                Export SUs Following PU
            </button> */}

            <button
                className="btn btn-primary float-right"
                onClick={handleRemoveSubscription}
            >
                Remove Subscriptions
            </button>
        </div>
        {gettingSuList
            ? <div className='mt-4'><Spinner size="sm" variant='primary' animation='border' /> Fetching data...</div>
            : ''}
        <div className="d-flex flex-row justify-content-between align-items-center my-3">
            <div className="col-md-6">
                {total > 0 && PaginationContainer()}
            </div>
            <div className="col-md-6 text-right">
                <strong>{selectedFollower.length} Selected SUs</strong>
            </div>
        </div>
        <div className="my-2">
            <strong>FOLLOWER LIST</strong>
        </div>
        <table className="table table-sm table-bordered table-striped table-hover mt-2 su-list">
            <thead>
                <tr>
                    {
                        tableHeaders.map((header, index) => {
                            if (header.sortable) {
                                return <th key={`${header.column}-${index}`} width={header.width}>
                                    {orderBy === header.column
                                        ? <>
                                            <a
                                                href="#"
                                                onClick={(e) => handleSorting(e, header.column)}
                                                className="active"
                                            >
                                                {header.name}

                                                <Sorting sort={sortBy} setSortBy={setSortBy} />
                                            </a>
                                        </>
                                        : <>
                                            <a
                                                href="#"
                                                onClick={(e) => handleSorting(e, header.column)}
                                            >
                                                {header.name}
                                            </a>
                                        </>
                                    }
                                </th>;
                            }

                            return <th key={`${header.column}-${index}`} width={header.width}>{header.name}</th>;
                        })
                    }
                </tr>
            </thead>
            <tbody>
                {
                    sus && sus.data && sus.data.length > 0 && sus.data.map(su => {
                        return <tr key={su.id} onClick={() => handleSelectFollower(su)}>
                            <td>
                                <input
                                    type="checkbox"
                                    value={su.id}
                                    onChange={(e) => handleSelectFollower(su)}
                                    checked={selectedFollower.indexOf(su.id) >= 0}
                                />
                            </td>
                            <td>
                                {su.id}
                            </td>
                            <td>
                                {su.su_login ? su.su_login : ''}
                            </td>
                            <td>
                                {su.pu_login ? su.pu_login : ''}
                            </td>
                            <td>
                                {su.plan_id}
                            </td>
                            <td>
                                {moment.utc(su.subscribed_at).local().format('lll')}
                            </td>
                            <td>
                                {
                                    su.amount > 0 ? currency(su.amount).format({ precision: 2 }) : ''
                                }
                            </td>

                        </tr>;
                    })
                }

                {
                    !sus || !sus.data || sus.data.length <= 0 && <tr>
                        <td colSpan="7" align='center'>
                            No SU found
                        </td>
                    </tr>
                }
            </tbody>
        </table>
    </React.Fragment>;
};

export default React.memo(SuList);
